import {useUpmPlant} from '@hconnect/upm'
import {Button, DashboardCard, Icon, Loading} from '@hm/fundamental'
import {mdiArrowCollapseLeft, mdiArrowCollapseRight, mdiChevronLeft} from '@mdi/js'
import moment from 'moment'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {DetailDrawing} from './DetailDrawing'
import {DetailHeader} from './DetailHeader'
import {DetailSections} from './DetailSections'

import {useShutdowns} from '@refra/modules/common/providers/ShutdownsProvider'
import {
  useStaticPartComponentDetailQuery,
  useStaticPartDetailQuery,
  useStaticPartReplacementQuery
} from '@refra/modules/refra-static-parts/hooks/queries'
import {
  EditReplacementProvider,
  DetailLayoutProvider
} from '@refra/modules/refra-static-parts/providers'
import {useUrlParam} from '@refra/modules/routing/hooks'

const SECTIONS_DRAWER_WIDTH = '600px'

export const StaticPartReplacementEditDetail = () => {
  const staticPartId = useUrlParam('staticPartId')
  const plantCode = useUrlParam('plantCode')
  const selectedKilnId = useUrlParam('selectedKilnId')
  const shutdownId = useUrlParam('shutdownId')
  const componentId = parseInt(useUrlParam('componentId'))
  const upmPlant = useUpmPlant({plantId: plantCode})
  const navigate = useNavigate()
  const {t} = useTranslation()
  const [isSectionsDrawerOpen, setIsSectionsDrawerOpen] = useState(true)

  const {selectedShutdown} = useShutdowns()

  const {data: staticPart, isLoading: isStaticPartLoading} = useStaticPartDetailQuery({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKilnId,
    staticPartId: staticPartId,
    shutdownId: parseInt(shutdownId)
  })

  const {data: selectedComponent, isLoading: isStaticPartComponentLoading} =
    useStaticPartComponentDetailQuery({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKilnId,
      staticPartId: staticPartId,
      componentId,
      shutdownId: parseInt(shutdownId)
    })

  const {data: replacements, isLoading: isReplacementLoading} = useStaticPartReplacementQuery({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKilnId,
    shutdownId: parseInt(shutdownId),
    componentId
  })

  if (isStaticPartComponentLoading || isReplacementLoading || isStaticPartLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loading size="xlarge" data-cy="static_part_detail_loader" />
      </div>
    )
  }

  const staticPartDetailUrl = `/plants/${plantCode}/static-parts/${selectedKilnId}/${staticPartId}`

  if (!selectedComponent || !staticPart) {
    void navigate(`${staticPartDetailUrl}/edit`)
    return null
  }

  const replacement = replacements ? replacements[0] : undefined
  const detailLayoutValue = {
    isSectionsDrawerOpen,
    sectionsDrawerWidth: SECTIONS_DRAWER_WIDTH
  }

  return (
    <EditReplacementProvider selectedComponent={selectedComponent} replacement={replacement}>
      <DetailLayoutProvider value={detailLayoutValue}>
        <div className="flex flex-col gap-6">
          <DashboardCard data-cy="refra_static_parts-edit-replacement_card">
            <div
              className="flex flex-col gap-6"
              data-cy="refra_static_parts-edit-replacement_card_content"
            >
              <div className="flex flex-col">
                <div>
                  <Button
                    variant="tertiary"
                    size="small"
                    icon={mdiChevronLeft}
                    onClick={() => void navigate(staticPartDetailUrl, {replace: true})}
                  >
                    {t('staticParts.editReplacement.back')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <DetailHeader
                staticPart={staticPart}
                selectedComponent={selectedComponent}
                interventionDate={
                  selectedShutdown?.interventionDate
                    ? moment(selectedShutdown.interventionDate).format('DD.MM.YYYY')
                    : ''
                }
              />
            </div>
          </DashboardCard>

          <DashboardCard data-cy="refra_static_parts-edit-replacement_card_drawing">
            <div className="relative w-full h-full overflow-hidden">
              <div
                className="absolute left-0 top-0 bottom-0 z-20 max-w-[92vw] transition-transform duration-300 ease-out"
                style={{
                  width: SECTIONS_DRAWER_WIDTH,
                  transform: isSectionsDrawerOpen
                    ? 'translateX(0)'
                    : 'translateX(calc(-100% + 1rem))'
                }}
              >
                <div
                  className="relative h-[calc(100vh-400px)] p-6 m-4 rounded-lg border border-gray-100 bg-white shadow-md"
                  data-cy="refra_static_parts-edit-replacement_card_sections"
                >
                  <div className="h-full overflow-y-auto">
                    <DetailSections />
                  </div>

                  <Button
                    variant="secondary"
                    icon={isSectionsDrawerOpen ? mdiArrowCollapseLeft : mdiArrowCollapseRight}
                    className="absolute right-0 top-4 translate-x-full shadow-lg rounded-none rounded-r-lg border-none"
                    onClick={() => setIsSectionsDrawerOpen((current) => !current)}
                  />
                </div>
              </div>

              <div className="w-full h-full">
                <DetailDrawing selectedComponent={selectedComponent} />
              </div>
            </div>
          </DashboardCard>
        </div>
      </DetailLayoutProvider>
    </EditReplacementProvider>
  )
}
